'use client';

import { contactFormSchema, ContactFormSchemaType } from './ContactForm.utils';
import { css } from '@onbeam/business-styled-system/css';
import { text, vstack } from '@onbeam/business-styled-system/patterns';
import { token } from '@onbeam/business-styled-system/tokens';
import { Button, Input, Textarea, toast } from '@onbeam/business-ui';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useBreakpoint } from '@onbeam/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { CheckIcon } from '@onbeam/icons/business';

const ZENDESK_REQUEST_URL =
  'https://gamingmeritcircle.zendesk.com/api/v2/requests.json';

export const ContactForm = () => {
  const [showSuccess, setShowSuccess] = useState(false);
  const isMobile = useBreakpoint({ max: token('breakpoints.md') });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormSchemaType>({
    resolver: zodResolver(contactFormSchema),
  });

  const onSubmit: SubmitHandler<ContactFormSchemaType> = async ({
    name,
    email,
    message,
  }) => {
    try {
      const response = await fetch(ZENDESK_REQUEST_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          request: {
            subject: 'Message via onbeam.com',
            requester: { name, email },
            comment: { body: message },
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      toast.success('Thank you for your submission', {
        description: "We'll review it and get back to you.",
      });
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 2500);
      reset();
    } catch {
      toast.error('Something went wrong', {
        description: 'Please try again later.',
      });
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={vstack({
        gap: '6',
        maxW: '[42rem]',
        w: 'full',
        mx: 'auto',
        mb: '10',
        md: {
          p: '10',
          border: '1px solid token(colors.opacity.md)',
          rounded: 'lg',
          shadow: 'md',
          gap: '4',
        },
      })}
    >
      <h2 className={text({ style: 'subtitle-sm', mb: '2' })}>Contact form</h2>
      <div className={vstack({ gap: '6', md: { flexDir: 'row', gap: '4' } })}>
        <Input
          variant="labelWithin"
          label="Name"
          errorMessage={errors.name?.message}
          {...register('name')}
        />
        <Input
          variant="labelWithin"
          label="Email"
          errorMessage={errors.email?.message}
          {...register('email')}
        />
      </div>
      <Textarea
        placeholder="Provide additional information"
        rows={6}
        errorMessage={errors.message?.message}
        {...register('message')}
      />
      <Button
        type="submit"
        className={css({ md: { ml: 'auto' } })}
        isLoading={isSubmitting}
        {...(isMobile && { size: 'fullWidth' })}
        {...(showSuccess && {
          color: 'green',
          variant: 'secondary',
          iconLeft: <CheckIcon />,
          isInteractive: false,
        })}
      >
        {showSuccess ? 'Submitted' : 'Submit'}
      </Button>
    </form>
  );
};
